/**
 * The one way the app prints a generated page: open a window, write the page,
 * and open the print dialog once every image on it has settled.
 *
 * Every print view used to do this itself — some waited a fixed 300–600ms for
 * the logo, one waited for images, one printed at once — so a slow logo or a
 * photo column printed blank depending on which screen it came from.
 */

/** Longest the print dialog waits for a stuck image before printing anyway. */
export const PRINT_IMAGE_TIMEOUT_MS = 8000;

export interface PrintDocumentOptions {
  /** Size of the print window; the browser may ignore it. */
  width?: number;
  height?: number;
}

/**
 * Open a window holding `html` and print it. Returns the window, or null when
 * the browser blocked the pop-up (nothing is printed then).
 */
export function printDocument(html: string, options: PrintDocumentOptions = {}): Window | null {
  const { width = 900, height = 800 } = options;
  const win = window.open('', '_blank', `width=${width},height=${height}`);
  if (!win) return null;
  win.document.write(html);
  win.document.close();
  printWhenReady(win);
  return win;
}

/**
 * Print once the page has loaded and every image in it has loaded or failed,
 * so a logo, a photo cell or a floor plan's traced background (an SVG
 * `<image>`, which `document.images` does not list) is never blank on paper.
 * A stuck image never holds the dialog up for longer than
 * {@link PRINT_IMAGE_TIMEOUT_MS}.
 */
export function printWhenReady(win: Window): void {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    win.focus();
    win.print();
  };

  const waitForImages = () => {
    const images: HTMLImageElement[] = win.document?.images ? Array.from(win.document.images) : [];
    const pending = images.filter((img) => !img.complete);
    if (pending.length === 0) {
      finish();
      return;
    }
    let remaining = pending.length;
    const settle = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    pending.forEach((img) => {
      img.addEventListener('load', settle);
      img.addEventListener('error', settle);
    });
  };

  // The window's load event covers every resource on the page, SVG images
  // included; `document.images` is checked too, for a page already loaded.
  const loading = win.document?.readyState && win.document.readyState !== 'complete';
  if (loading && typeof win.addEventListener === 'function') {
    win.addEventListener('load', waitForImages, { once: true });
  } else {
    waitForImages();
  }
  setTimeout(finish, PRINT_IMAGE_TIMEOUT_MS);
}
